import { useState, useEffect } from "react";
import { Sword, Link as LinkIcon, Play, Pause, Trophy, CaretLeft } from "@phosphor-icons/react";
import { useAuth } from "../hooks/useAuth";
import { useGameEngine } from "../hooks/useGameEngine";
import { StageIndicator } from "../components/StageIndicator";
import { isGuessCorrect } from "../lib/guessMatch";
import { GuessInput } from "../components/GuessInput";
import { VinylRecord } from "../components/VinylRecord";
import { PuzzleCard } from "../components/PuzzleCard";
import { createChallenge, joinChallenge, saveH2HScore, getH2HResults, completeChallenge, getRecentChallenges, deleteChallenge, getH2HLiveState, rollupH2HTotals, getChallengeSongs, subscribeToH2H, notifyOpponentOfFinish, type H2HSong, type H2HRole, type H2HLiveState } from "../lib/h2h";
import { subscribeToPush } from "../lib/pushSubscription";
import { supabase } from "../lib/supabase";
import { trackEvent } from "../lib/clarityEvents";
import { useLocale } from "../lib/i18n";
import { generateH2HInviteText } from "../lib/h2hShare";

type Phase = "menu" | "waiting" | "playing" | "song-result" | "final-results";

const h2hOptions = { revealMode: "notes" as const };
export function HeadToHead({ onNavigate }: { onNavigate?: (tab: string) => void } = {}) {
  const auth = useAuth();
  const { t, locale } = useLocale();
  const engine = useGameEngine(h2hOptions);
  const [phase, setPhase] = useState<Phase>("menu");
  const [code, setCode] = useState("");
  const [joinInput, setJoinInput] = useState("");
  const [h2hId, setH2hId] = useState("");
  const [songs, setSongs] = useState<H2HSong[]>([]);
  const [songIndex, setSongIndex] = useState(0);
  const [guesses, setGuesses] = useState<string[]>([]);
  const [wrongSignal, setWrongSignal] = useState(0);
  const [scores, setScores] = useState<number[]>([]);
  const [h2hLoading, setH2hLoading] = useState(false);
  const [h2hError, setH2hError] = useState<string | null>(null);
  const [results, setResults] = useState<Awaited<ReturnType<typeof getH2HResults>>>(null);
  const [recentDuels, setRecentDuels] = useState<Awaited<ReturnType<typeof getRecentChallenges>>>([]);
  const [songCount, setSongCount] = useState<1 | 3 | 5>(3);
  const [selectedPlaylist, setSelectedPlaylist] = useState<string | null>(null);
  const [playlists, setPlaylists] = useState<{ id: string; name: string }[]>([]);
  // Role of the local player in the current challenge — drives which side of
  // the live state counts as "mine" vs "opponent".
  const [role, setRole] = useState<H2HRole | null>(null);
  // Latest live snapshot from the h2h_get_state RPC (null until first poll).
  const [liveState, setLiveState] = useState<H2HLiveState | null>(null);
  // Live opponent total, updated by the poll. 0 when we don't know yet.
  const [liveOpponentTotal, setLiveOpponentTotal] = useState(0);
  // Toast shown when the opponent just finished their side of the match.
  const [opponentFinishedToast, setOpponentFinishedToast] = useState(false);
  // 2-second visual confirmation after tapping the "Copy code" button.
  const [codeCopied, setCodeCopied] = useState(false);

  useEffect(() => {
    if (auth.user) {
      getRecentChallenges().then(setRecentDuels).catch(e => console.error("Failed to load duels:", e));
    }
    supabase.from("playlists").select("id, name").gte("song_count", 150).order("name").then(({ data }) => {
      setPlaylists(data ?? []);
    });
  }, [auth.user]);

  // Unified live-state listener covering every in-match phase.
  //  • waiting       → auto-start when opponent joins
  //  • playing       → live opponent total strip
  //  • song-result   → same
  //  • final-results → live opponent total + opponent-finished notification
  //
  // Primary path: Supabase Realtime subscription on h2h_challenges and
  // h2h_scores. Every postgres_changes event triggers a re-fetch of the
  // full joined state via getH2HLiveState.
  //
  // Fallback: a 20-second low-frequency interval in case the websocket
  // drops without notifying us (offline, bad network, server restart).
  // 20s is low enough to catch stuck sessions within a song's length but
  // high enough to be nearly free at scale.
  useEffect(() => {
    if (phase === "menu" || !code || !role) return;
    let cancelled = false;
    const prevOpponentFinished = { current: false };

    const refresh = async () => {
      const state = await getH2HLiveState(code);
      if (cancelled || !state) return;
      setLiveState(state);

      const rollup = rollupH2HTotals(state, role);
      setLiveOpponentTotal(rollup.opponentTotal);

      // Auto-advance from waiting → playing once opponent has joined.
      if (phase === "waiting" && state.status !== "pending" && songs[0]) {
        await startSong(songs[0]);
      }

      // Fire notification the first time we observe the opponent finished.
      if (rollup.opponentFinished && !prevOpponentFinished.current) {
        prevOpponentFinished.current = true;
        notifyOpponentFinished(rollup.opponentTotal);
      }
    };

    // Immediate first fetch so the UI populates before any events arrive.
    refresh();

    // Subscribe only if we know the h2hId (which we always do in non-menu
    // phases — it's set at join/create/open time).
    const unsubscribe = h2hId ? subscribeToH2H(h2hId, refresh) : undefined;

    // Fallback poll, 20s, in case the websocket silently drops.
    const fallbackInterval = window.setInterval(refresh, 20000);

    return () => {
      cancelled = true;
      unsubscribe?.();
      window.clearInterval(fallbackInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, code, role, h2hId, songs]);

  /** Show an in-app toast and, if permission was already granted, a native
   *  browser notification so the user sees it even in a backgrounded tab. */
  const notifyOpponentFinished = (opponentTotal: number) => {
    setOpponentFinishedToast(true);
    window.setTimeout(() => setOpponentFinishedToast(false), 6000);
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        new Notification(t("h2h.opponentFinishedTitle"), {
          body: t("h2h.opponentFinishedBody", { score: opponentTotal }),
          icon: "/icons/icon-192.png",
          tag: "h2h-opponent-finished",
        });
      }
    } catch {
      // best-effort only — tab may be in a context without Notification support
    }
    trackEvent("h2h_opponent_finished");
  };

  useEffect(() => {
    const joinCode = window.__h2hJoinCode;
    if (joinCode) {
      delete window.__h2hJoinCode;
      setJoinInput(joinCode);
      (async () => {
        setH2hLoading(true);
        const result = await joinChallenge(joinCode);
        setH2hLoading(false);
        if (!result) {
          setH2hError(t("h2h.challengeNotFound"));
          return;
        }
        setH2hId(result.id);
        setSongs(result.songs);
        setSongIndex(0);
        setScores([]);
        setCode(joinCode);
        setLiveOpponentTotal(0);
        setLiveState(null);
        // Challenger tapped their own invite link: resume rather than join.
        if (result.isSelf) {
          setRole("challenger");
          setPhase(result.status === "pending" ? "waiting" : "playing");
          if (result.status !== "pending") {
            await startSong(result.songs[0]);
          }
          return;
        }
        setRole(auth.user ? "opponent" : "guest");
        await startSong(result.songs[0]);
      })();
      return;
    }

    // "Challenge Back" from share landing — challenge already created, go to waiting phase
    const challengerCode = window.__h2hChallengerCode;
    if (challengerCode) {
      delete window.__h2hChallengerCode;
      setCode(challengerCode);
      setRole("challenger");
      setPhase("waiting");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (auth.loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-10 h-10 rounded-full border-3 border-zinc-700 border-t-purple-500 animate-spin" />
      </div>
    );
  }

  // Guests can join via code but not create
  const isGuest = !auth.user;

  const handleCreate = async () => {
    setH2hLoading(true);
    setH2hError(null);
    const result = await createChallenge({ songCount, playlistId: selectedPlaylist ?? undefined });
    setH2hLoading(false);
    if (!result) {
      setH2hError(t("h2h.createFailed"));
      return;
    }
    trackEvent("h2h_sent");
    setCode(result.code);
    setH2hId(result.id);
    setSongs(result.songs);
    setRole("challenger");
    setLiveOpponentTotal(0);
    setLiveState(null);
    setPhase("waiting");
    // Best-effort: register a web-push subscription so the server can
    // deliver a real notification when the opponent finishes, even if the
    // tab is closed or the browser backgrounded. Prompts for permission
    // on first call; silently no-ops if the user denied it before.
    // Gated by the H2H push feature flag.
    if (import.meta.env.VITE_H2H_PUSH_ENABLED !== "false") {
      subscribeToPush().catch(() => { /* ignore — best-effort */ });
    }

    // Auto-open WhatsApp with invite
    const url = `${window.location.origin}/#h2h/${result.code}`;
    const text = generateH2HInviteText(url, locale === "he" ? "he" : "en", songCount);
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const startSong = async (song: H2HSong) => {
    setGuesses([]);
    setPhase("playing");
    trackEvent("game_start");
    try {
      await engine.loadAndPlay(song.artist, song.title, { kind: "random" });
    } catch {
      // Skip unplayable song
      handleSongEnd(0, 0);
    }
  };

  const handleSongEnd = async (stage: number, score: number) => {
    trackEvent("h2h_played");
    await saveH2HScore(h2hId, songIndex, stage, score);
    const newScores = [...scores, score];
    setScores(newScores);
    setPhase("song-result");

    // If all songs done, mark my side complete and hand off to final-results.
    // Live polling (h2h_get_state) supplies the opponent's running total from
    // there — it works for guest opponents too, unlike the old getH2HResults.
    if (songIndex >= songs.length - 1) {
      await completeChallenge(h2hId);
      // Fire-and-forget push notification to the other participant. Gated
      // by VITE_H2H_PUSH_ENABLED and silently no-ops for guest callers.
      notifyOpponentOfFinish(h2hId);
      // Back-compat snapshot (authed users only). The live poll overrides
      // this as soon as it runs.
      const res = await getH2HResults(h2hId);
      setResults(res);
      setPhase("final-results");
    }
  };

  const handleNextSong = async () => {
    const next = songIndex + 1;
    setSongIndex(next);
    await startSong(songs[next]);
  };

  const handleGuess = async (artist: string, title: string) => {
    if (!engine.track) return;
    const currentHE = songs[songIndex];
    const { titleMatch, artistMatch } = isGuessCorrect(engine.track.track.title, engine.track.track.artist, title, artist, currentHE?.title_he, currentHE?.artist_he);

    if (titleMatch && artistMatch) {
      setGuesses([...guesses, "correct"]);
      await engine.reveal();
      handleSongEnd(engine.stage, engine.score);
    } else {
      const newGuesses = [...guesses, "wrong"];
      setGuesses(newGuesses);
      setWrongSignal((s) => s + 1);
      const hasNext = await engine.skip();
      if (!hasNext) {
        await engine.reveal();
        handleSongEnd(0, 0);
      }
    }
  };

  const handleSkip = async () => {
    const newGuesses = [...guesses, "skip"];
    setGuesses(newGuesses);
    const hasNext = await engine.skip();
    if (!hasNext) {
      await engine.reveal();
      handleSongEnd(0, 0);
    }
  };

  const handleShareWhatsApp = () => {
    const url = `${window.location.origin}/#h2h/${code}`;
    const text = generateH2HInviteText(url, locale === "he" ? "he" : "en", songs.length || songCount);
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    trackEvent("h2h_whatsapp_share");
  };

  /** Open a past / in-progress challenge from the "recent duels" list.
   *  Loads the songs + live state, populates the local match state, and
   *  jumps straight to the final-results screen. Live polling takes over
   *  from there, so an active match updates in real time while the user
   *  watches. Works for both challenger and opponent perspectives. */
  const handleOpenChallenge = async (duel: { id: string; code: string; is_challenger: boolean }) => {
    const [duelSongs, state] = await Promise.all([
      getChallengeSongs(duel.id),
      getH2HLiveState(duel.code),
    ]);
    if (!duelSongs || !state) return;

    const myRole: H2HRole = duel.is_challenger ? "challenger" : "opponent";
    const mySide = duel.is_challenger ? state.challenger_scores : state.opponent_scores;
    // Rebuild the local per-song scores array so the Playing/Song-result
    // screens would read a consistent total if the user navigates backwards.
    const mySongScores = Array(duelSongs.length).fill(0);
    mySide.forEach((s) => { if (s.song_index < duelSongs.length) mySongScores[s.song_index] = s.score; });

    setH2hId(duel.id);
    setCode(duel.code);
    setSongs(duelSongs);
    setSongIndex(duelSongs.length - 1);
    setScores(mySongScores);
    setRole(myRole);
    setLiveState(state);
    setLiveOpponentTotal(rollupH2HTotals(state, myRole).opponentTotal);
    setPhase("final-results");
  };

  const isPlaying = engine.playback.status === "playing";
  const myLiveTotal = scores.reduce((a, b) => a + b, 0);

  /** Shared live opponent strip shown during playing / song-result so the
   *  player can see the opponent's running total update in real time. */
  const liveOpponentStrip = (
    <div className="w-full flex items-center justify-between text-[11px] font-bold uppercase tracking-wider mb-2 px-1">
      <span className="text-purple-400">{t("h2h.you")} <span className="text-white tabular-nums">{myLiveTotal}</span></span>
      <span className="text-zinc-600">vs</span>
      <span className="text-zinc-400">
        {t("h2h.opponent")}{" "}
        <span className="text-white tabular-nums">{liveOpponentTotal}</span>
        {liveState && (
          (role === "challenger"
            ? liveState.opponent_completed_at || liveState.guest_completed_at
            : liveState.challenger_completed_at)
        ) && <span className="ml-1 text-green-400">✓</span>}
      </span>
    </div>
  );

  /** In-app toast shown for ~6s when the opponent just finished. Browsers
   *  that granted Notification permission also get a native OS notification
   *  via notifyOpponentFinished — this covers the foregrounded-tab case. */
  const opponentFinishedBanner = opponentFinishedToast && (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl bg-purple-600 text-white font-bold text-sm shadow-lg shadow-purple-900/40 animate-pulse">
      {t("h2h.opponentFinishedBody", { score: liveOpponentTotal })}
    </div>
  );

  // ── Menu ──
  if (phase === "menu") {
    const dir = locale === "he" ? "rtl" : "ltr";
    return (
      <div dir={dir} className="px-5 pt-3 pb-6 flex flex-col gap-5" style={{ color: "#fff" }}>
        {/* Back header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => onNavigate?.("home")}
            className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em]"
            style={{ color: "rgba(255,255,255,0.55)" }}
          >
            <CaretLeft size={12} weight="bold" />
            {locale === "he" ? "ראש בראש" : "Head 2 Head"}
          </button>
        </div>

        <div className="text-center">
          <h2 className="text-[22px] font-extrabold text-white" style={{ letterSpacing: -0.4 }}>{t("h2h.challengeFriend")}</h2>
        </div>

        {/* Create section — requires auth */}
        {isGuest ? (
          <button onClick={auth.signInWithGoogle} className="btn-ghost w-full py-4 rounded-2xl font-bold text-sm text-zinc-400">
            {t("h2h.signInCreate")}
          </button>
        ) : (
          <>
            {/* Song count picker — PuzzleCards */}
            <div className="w-full">
              <p className="text-[13px] font-extrabold mb-2.5" style={{ textAlign: dir === "rtl" ? "right" : "left", color: "rgba(255,255,255,0.9)" }}>{t("h2h.songs")}</p>
              <div className="grid grid-cols-3 gap-2.5">
                {([1, 3, 5] as const).map((n) => {
                  const subtitles: Record<number, string> = { 1: t("h2h.quickDuel"), 3: t("h2h.bestOf3"), 5: t("h2h.fullBattle") };
                  const titles: Record<number, string> = { 1: t("h2h.song", { n }), 3: t("h2h.songPlural", { n }), 5: t("h2h.songPlural", { n }) };
                  return (
                    <PuzzleCard
                      key={n}
                      badge={String(n)}
                      title={titles[n]}
                      subtitle={subtitles[n]}
                      accent="#00E0FF"
                      selected={songCount === n}
                      onClick={() => setSongCount(n)}
                      dir={dir}
                    />
                  );
                })}
              </div>
            </div>

            {/* Playlist selector */}
            {playlists.length > 0 && (
              <div className="w-full">
                <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-center mb-2">{t("h2h.playlist")}</p>
                <select
                  value={selectedPlaylist ?? ""}
                  onChange={(e) => setSelectedPlaylist(e.target.value || null)}
                  className="search-dark w-full py-4 px-5 rounded-2xl font-bold text-sm"
                >
                  <option value="">{t("h2h.randomAll")}</option>
                  {playlists.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}

            <button onClick={handleCreate} disabled={h2hLoading} className="btn-primary w-full py-5 rounded-2xl font-bold text-base flex items-center justify-center gap-3 disabled:opacity-50">
              {h2hLoading ? t("h2h.creating") : <><Sword size={22} weight="fill" /> {t("h2h.createChallenge")}</>}
            </button>
          </>
        )}

        {h2hError && <p className="text-sm text-red-400 text-center">{h2hError}</p>}

        {/* Join by code — works for everyone */}
        <div className="w-full flex items-center gap-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[11px] font-bold text-zinc-500 uppercase">{isGuest ? t("h2h.joinChallenge") : t("common.or")}</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
        <div className="w-full flex gap-2">
          <input
            value={joinInput}
            onChange={(e) => setJoinInput(e.target.value.toUpperCase())}
            placeholder={t("common.enterCode")}
            maxLength={6}
            className="search-dark flex-1 py-3 px-4 rounded-xl font-bold text-center text-sm tracking-[0.3em] uppercase"
          />
          <button
            onClick={async () => {
              setH2hLoading(true);
              setH2hError(null);
              const result = await joinChallenge(joinInput);
              setH2hLoading(false);
              if (!result) { setH2hError(t("h2h.invalidCode")); return; }
              setH2hId(result.id);
              setSongs(result.songs);
              setSongIndex(0);
              setScores([]);
              setCode(joinInput);
              setLiveOpponentTotal(0);
              setLiveState(null);
              if (result.isSelf) {
                setRole("challenger");
                setPhase(result.status === "pending" ? "waiting" : "playing");
                if (result.status !== "pending") await startSong(result.songs[0]);
                return;
              }
              setRole(auth.user ? "opponent" : "guest");
              await startSong(result.songs[0]);
            }}
            disabled={joinInput.length < 6}
            className="btn-ghost px-4 rounded-xl disabled:opacity-40"
          >
            {t("common.join")}
          </button>
        </div>

        {!isGuest && recentDuels.length > 0 && (
          <div className="w-full mt-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-center mb-3">{t("h2h.recentDuels")}</p>
            <div className="flex flex-col gap-2">
              {recentDuels.map((d) => (
                <div key={d.id} className="glass rounded-xl px-4 py-3 flex items-center justify-between">
                  <div>
                    <span className="text-sm font-bold text-white tracking-wider">{d.code}</span>
                    <span className={`ms-2 text-[11px] font-bold uppercase ${
                      d.status === "completed" ? "text-green-400" : d.status === "active" ? "text-purple-400" : "text-zinc-500"
                    }`}>{d.status}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Pending + challenger: WhatsApp share. Other states: view result. */}
                    {d.status === "pending" && d.is_challenger && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = `${window.location.origin}/#h2h/${d.code}`;
                          const text = generateH2HInviteText(url, locale === "he" ? "he" : "en", d.song_count);
                          window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
                        }}
                        className="text-[11px] font-bold text-[#25D366] hover:text-[#20BD5A]"
                      >
                        {t("h2h.whatsapp")} <LinkIcon size={12} className="inline" />
                      </button>
                    )}
                    {(d.status === "active" || d.status === "completed") && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleOpenChallenge(d); }}
                        className="text-[11px] font-bold text-purple-300 hover:text-white"
                      >
                        {t("h2h.viewResult")}
                      </button>
                    )}
                    {/* Delete is available for all states now (RLS fixed in mig 015). */}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        await deleteChallenge(d.id);
                        setRecentDuels((prev) => prev.filter((x) => x.id !== d.id));
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-full text-base font-bold text-red-400/70 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                      aria-label={t("h2h.deleteChallenge")}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Waiting ──
  if (phase === "waiting") {
    return (
      <div className="px-5 pt-5 pb-6 flex flex-col items-center gap-6">
        <div className="text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">{t("h2h.title")}</p>
          <h2 className="text-xl font-bold text-white mt-1">{t("h2h.waiting")}</h2>
        </div>

        <div className="w-full glass rounded-2xl py-6 px-4 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2">{t("h2h.shareLink")}</p>
          <p className="text-3xl font-bold text-white tracking-[0.4em] tabular-nums mb-3">{code}</p>
          <button
            onClick={async () => {
              const url = `${window.location.origin}/#h2h/${code}`;
              try {
                await navigator.clipboard.writeText(url);
                setCodeCopied(true);
                window.setTimeout(() => setCodeCopied(false), 2000);
              } catch { /* clipboard blocked — silently ignore */ }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-purple-300 hover:text-white bg-purple-500/10 hover:bg-purple-500/20 transition-colors mb-2"
          >
            {codeCopied ? t("h2h.linkCopied") : t("h2h.copyLink")}
          </button>
          <p className="text-xs text-zinc-500 break-all">{window.location.origin}/#h2h/{code}</p>
        </div>

        <button onClick={handleShareWhatsApp} className="w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-white transition-colors shadow-[0_4px_20px_rgba(37,211,102,0.3)]">
          <svg width={22} height={22} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          {t("h2h.sendWhatsApp")}
        </button>

        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${liveState && liveState.status !== "pending" ? "bg-green-400" : "bg-purple-500 animate-pulse"}`} />
          <p className="text-sm text-zinc-400">
            {liveState && liveState.status !== "pending" ? t("h2h.opponentJoined") : t("h2h.waitingJoin")}
          </p>
        </div>

        {/* Let challenger start playing while waiting */}
        <button
          onClick={() => { setSongIndex(0); setScores([]); startSong(songs[0]); }}
          className="btn-ghost px-6 py-3 rounded-xl text-sm font-bold"
        >
          {t("h2h.startNow")}
        </button>

        <button onClick={() => setPhase("menu")} className="text-zinc-500 text-sm font-medium">{t("common.cancel")}</button>
      </div>
    );
  }

  // ── Playing ──
  if (phase === "playing") {
    return (
      <div className="px-5 pt-4 pb-6 flex flex-col items-center">
        {opponentFinishedBanner}
        {/* Progress */}
        <div className="w-full flex items-center justify-between mb-3">
          <span className="text-[11px] font-bold text-purple-400 uppercase">{t("h2h.duel")}</span>
          <span className="text-[11px] font-bold text-zinc-500">{t("h2h.songProgress", { current: songIndex + 1, total: songs.length })}</span>
        </div>
        <div className="w-full h-1 rounded-full bg-white/5 mb-4 overflow-hidden">
          <div className="h-full bg-purple-500 transition-all" style={{ width: `${((songIndex) / songs.length) * 100}%` }} />
        </div>
        {liveOpponentStrip}

        <div className="w-full max-w-[220px] mb-4">
          <VinylRecord isPlaying={isPlaying} stage={engine.stage} size="sm" />
        </div>

        <div className="w-full mb-4">
          <StageIndicator currentStage={engine.stage} guesses={guesses} />
        </div>

        <div className="flex items-center gap-6 mb-4">
          <button onClick={() => isPlaying ? engine.pause() : engine.play()} disabled={!engine.ready && !isPlaying} className="btn-primary w-14 h-14 rounded-full flex items-center justify-center disabled:opacity-60">
            {isPlaying ? <Pause size={24} weight="fill" /> : <Play size={24} weight="fill" />}
          </button>
          <button onClick={handleSkip} className="btn-ghost px-4 py-2.5 rounded-xl font-bold text-xs">
            {engine.stage >= 6 ? t("common.discover") : t("common.clue")}
          </button>
        </div>

        <div className="w-full">
          <GuessInput onGuess={handleGuess} wrongSignal={wrongSignal} />
        </div>
      </div>
    );
  }

  // ── Song result (between songs) ──
  if (phase === "song-result") {
    const lastScore = scores[scores.length - 1];
    return (
      <div className="px-5 pt-5 pb-6 flex flex-col items-center gap-4">
        {opponentFinishedBanner}
        {liveOpponentStrip}
        <p className="text-[11px] font-bold text-zinc-500 uppercase">{t("h2h.songProgress", { current: songIndex + 1, total: songs.length })}</p>

        {engine.track && (
          <div className="text-center">
            <h3 className="text-lg font-bold text-white">
              {locale === "he" ? (songs[songIndex]?.title_he ?? engine.track.track.title) : (songs[songIndex]?.title_en ?? engine.track.track.title)}
            </h3>
            <p className="text-purple-400 font-medium text-sm">
              {locale === "he" ? (songs[songIndex]?.artist_he ?? engine.track.track.artist) : (songs[songIndex]?.artist_en ?? engine.track.track.artist)}
            </p>
          </div>
        )}

        <div className="glass rounded-2xl py-4 px-8 text-center">
          <p className="text-3xl font-bold text-white">+{lastScore}</p>
          <p className="text-xs text-zinc-500 mt-1">Total: {scores.reduce((a, b) => a + b, 0)}</p>
        </div>

        {songIndex < songs.length - 1 ? (
          <button onClick={handleNextSong} className="btn-primary w-full py-4 rounded-2xl font-bold text-base">
            {t("common.nextSong")}
          </button>
        ) : (
          <div className="w-8 h-8 rounded-full border-3 border-zinc-700 border-t-purple-500 animate-spin" />
        )}
      </div>
    );
  }

  // ── Final results ──
  if (phase === "final-results") {
    // Prefer the live rollup so totals update in real time while the
    // opponent is still playing. Fall back to the snapshot loaded on
    // completion, then to the local running total.
    const myTotal = liveState && role
      ? rollupH2HTotals(liveState, role).myTotal
      : results?.myTotal ?? myLiveTotal;
    const opTotal = liveState && role
      ? rollupH2HTotals(liveState, role).opponentTotal
      : results?.opponentTotal ?? liveOpponentTotal;
    const opponentDone = liveState && role
      ? rollupH2HTotals(liveState, role).opponentFinished
      : opTotal > 0;
    const won = myTotal > opTotal;
    const tied = myTotal === opTotal;

    return (
      <div className="px-5 pt-5 pb-6 flex flex-col items-center gap-5">
        {opponentFinishedBanner}
        <div className="text-center">
          <Trophy size={40} weight="fill" className={won && opponentDone ? "text-purple-400 mx-auto mb-2" : "text-zinc-500 mx-auto mb-2"} />
          <h2 className="text-2xl font-bold text-white">
            {!opponentDone ? t("h2h.scoresIn") : won ? t("h2h.youWon") : tied ? t("h2h.tie") : t("h2h.youLost")}
          </h2>
        </div>

        <div className="w-full glass rounded-2xl p-5 flex items-center justify-around">
          <div className="text-center">
            <p className="text-[11px] text-zinc-500 font-bold uppercase mb-1">{t("h2h.you")}</p>
            <p className="text-2xl font-bold text-purple-400">{myTotal}</p>
          </div>
          <div className="text-lg font-bold text-zinc-600">VS</div>
          <div className="text-center">
            <p className="text-[11px] text-zinc-500 font-bold uppercase mb-1">{t("h2h.opponent")}</p>
            <p className="text-2xl font-bold text-white">
              {opponentDone || opTotal > 0 ? opTotal : "..."}
            </p>
            {!opponentDone && (
              <p className="text-[11px] text-zinc-500 mt-1">{t("h2h.stillPlaying")}</p>
            )}
          </div>
        </div>

        {!opponentDone && (
          <p className="text-xs text-zinc-500 text-center">{t("h2h.opponentPending", { code })}</p>
        )}

        <button onClick={() => { setPhase("menu"); setRole(null); setLiveState(null); getRecentChallenges().then(setRecentDuels); }} className="btn-ghost w-full py-4 rounded-2xl font-bold">
          {t("h2h.backToH2H")}
        </button>
      </div>
    );
  }

  return null;
}
